import { DatePipe } from '@angular/common';
import { Component, DestroyRef, Input, computed, effect, inject, untracked } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription, timeout } from 'rxjs';
import { BlockedUser } from '../../models/blocked-user.model';
import { ApiService } from '../../services/api.service';
import { UserBlockService } from '../../services/user-block.service';

@Component({
  selector: 'app-user-safety-actions',
  standalone: true,
  imports: [TranslatePipe, DatePipe],
  styleUrl: './user-safety-actions.component.scss',
  template: `
    @if (!manage && blocks.blockStateReady() && blocks.canBlock(userId)) {
      <button
        type="button"
        class="btn btn-primary text-black d-inline-flex align-items-center gap-2"
        [disabled]="blocks.busy()"
        (click)="blocks.block(userId, userName || '')"
      >
        <i class="bi bi-ban" aria-hidden="true"></i>
        {{ 'USER_BLOCK' | translate }}
      </button>
    }
    @if (manage && blocks.user()) {
      <section class="my-3">
        @if (showHeading) {
          <h3>{{ 'USER_BLOCKED_USERS' | translate }}</h3>
        }
        @if (loading) {
          <p role="status">{{ 'LOADING' | translate }}</p>
        } @else if (error) {
          <p role="alert">{{ 'BLOCKED_USERS_LOAD_FAILURE' | translate }}</p>
          <button type="button" class="btn btn-primary text-black" (click)="load()">
            {{ 'RETRY' | translate }}
          </button>
        } @else if (!users.length) {
          <p role="status">{{ 'BLOCKED_USERS_EMPTY' | translate }}</p>
        } @else {
          <div class="blocked-list">
            <table class="blocked-table table table-dark table-hover align-middle mb-0">
              <caption class="visually-hidden">
                {{
                  'BLOCKED_AGENTS' | translate
                }}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{{ 'BLOCKED_AGENT_COLUMN' | translate }}</th>
                  <th scope="col" class="blocked-date">{{ 'BLOCKED_ON' | translate }}</th>
                  <th scope="col" class="blocked-action"></th>
                </tr>
              </thead>
              <tbody>
                @for (user of users.slice(0, visible); track user.userId) {
                  <tr>
                    <td>
                      <div class="d-flex align-items-center gap-2 gap-sm-3">
                        @if (user.officePhotoUrl && !failedImages.has(user.userId)) {
                          <img
                            [src]="user.officePhotoUrl"
                            alt=""
                            width="40"
                            height="40"
                            class="blocked-avatar rounded-circle object-fit-cover"
                            loading="lazy"
                            (error)="failedImages.add(user.userId)"
                          />
                        } @else {
                          <span
                            class="blocked-avatar blocked-initial rounded-circle"
                            aria-hidden="true"
                            >{{ (user.name || '?').charAt(0) }}</span
                          >
                        }
                        <span class="blocked-name">{{
                          user.name || ('BLOCKED_USER_UNNAMED' | translate)
                        }}</span>
                      </div>
                    </td>
                    <td class="blocked-date">
                      @if (user.blockedAt) {
                        <time [attr.datetime]="user.blockedAt" dir="ltr">{{
                          user.blockedAt | date: 'dd/MM/yyyy'
                        }}</time>
                      } @else {
                        <span>—</span>
                      }
                    </td>
                    <td class="blocked-action">
                      <button
                        type="button"
                        class="btn btn-primary text-black"
                        [disabled]="blocks.busy() || !blocks.canUnblock()"
                        (click)="unblock(user)"
                      >
                        {{ 'USER_UNBLOCK' | translate }}
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          @if (visible < users.length) {
            <button
              type="button"
              class="btn btn-primary text-black mt-3"
              (click)="visible = visible + 20"
            >
              {{ 'MAP_LOAD_MORE' | translate }}
            </button>
          }
        }
      </section>
    }
  `,
})
export class UserSafetyActionsComponent {
  @Input() userId = 0;
  @Input() userName?: string;
  @Input() manage = false;
  @Input() showHeading = true;
  readonly blocks = inject(UserBlockService);
  private readonly actorId = computed(() => this.blocks.user()?.id);
  private readonly api = inject(ApiService);
  users: BlockedUser[] = [];
  error = false;
  loading = false;
  visible = 20;
  failedImages = new Set<number>();
  private request?: Subscription;
  constructor() {
    inject(DestroyRef).onDestroy(() => this.request?.unsubscribe());
    effect(() => {
      const actor = this.actorId();
      untracked(() => {
        this.request?.unsubscribe();
        this.users = [];
        this.error = false;
        this.loading = false;
        this.visible = 20;
        this.failedImages.clear();
        if (this.manage && actor) this.load();
        else if (actor) void this.blocks.refreshBlockedUsers();
      });
    });
  }
  load(): void {
    const actor = this.blocks.user()?.id;
    if (!actor) return;
    this.request?.unsubscribe();
    this.loading = true;
    this.error = false;
    this.request = this.api
      .getBlockedUsers()
      .pipe(timeout(15000))
      .subscribe({
        next: (users) => {
          if (this.blocks.user()?.id === actor) {
            this.users = users;
            this.loading = false;
          }
        },
        error: () => {
          if (this.blocks.user()?.id === actor) {
            this.error = true;
            this.loading = false;
          }
        },
      });
  }
  async unblock(user: { userId: number; name: string | null }): Promise<void> {
    const actor = this.blocks.user()?.id;
    if (
      (await this.blocks.unblock(user.userId, user.name || '')) &&
      actor === this.blocks.user()?.id
    ) {
      this.request?.unsubscribe();
      this.loading = false;
      this.users = this.users.filter((row) => row.userId !== user.userId);
    }
  }
}
